import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Layer from '@arcgis/core/layers/Layer';
import type { AiLayerPickPayload } from './aiMapPick.types';
import { getExternalFeatureTitle } from './getExternalFeatureTitle';
import { graphicToAttributeSnapshot } from 'components/DisasterGPT/utils/layerSnapshotCompact';
import { internalRowToAttributeSnapshot } from 'components/DisasterGPT/utils/layerSnapshotCompact';
import {
  isAreaGeometry,
  type AiSpatialFilter,
} from 'components/DisasterGPT/utils/aiMapSpatialFilter';
import type { Geometry } from 'geojson';

export type ResolvedMapLayer = {
  kind: 'internal' | 'external';
  layerId: string;
  versionId?: string;
};

function layerTreeContains(root: __esri.Layer, target: __esri.Layer): boolean {
  if (root === target) return true;
  const group = root as __esri.Layer & {
    allLayers?: { toArray: () => __esri.Layer[] };
    layers?: { toArray: () => __esri.Layer[] };
  };
  const children = group.allLayers?.toArray() ?? group.layers?.toArray() ?? [];
  return children.some((child) => layerTreeContains(child, target));
}

function ancestorChain(layer: __esri.Layer | null | undefined): __esri.Layer[] {
  const chain: __esri.Layer[] = [];
  let current: __esri.Layer | null | undefined = layer;
  while (current) {
    chain.push(current);
    current = (current as { parent?: __esri.Layer | null }).parent ?? null;
  }
  return chain;
}

/** Map a hit graphic's ArcGIS layer back to a persisted Layers-tab id. */
export function resolveHitToPersistedLayer(
  graphicLayer: unknown,
  addedExternalLayers: Map<string, __esri.Layer[]>,
  internalGraphicsByVersion: Map<string, GraphicsLayer>,
  versionByInternalLayerId: Record<string, { id: string }>
): ResolvedMapLayer | null {
  if (!(graphicLayer instanceof Layer)) return null;
  const chain = ancestorChain(graphicLayer);

  for (const [layerId, layers] of addedExternalLayers) {
    for (const root of layers) {
      if (
        chain.some((node) => node === root || layerTreeContains(root, node))
      ) {
        return { kind: 'external', layerId };
      }
    }
  }

  for (const [versionId, graphicsLayer] of internalGraphicsByVersion) {
    if (!chain.includes(graphicsLayer)) continue;
    const layerId = Object.entries(versionByInternalLayerId).find(
      ([, version]) => String(version.id) === String(versionId)
    )?.[0];
    if (!layerId) continue;
    return { kind: 'internal', layerId, versionId };
  }

  return null;
}

export function getGraphicObjectId(
  graphic: {
    getObjectId?: () => number | string | null | undefined;
    attributes?: Record<string, unknown> | null;
  },
  objectIdField?: string
): string | number | null {
  try {
    const fromMethod = graphic.getObjectId?.();
    if (fromMethod != null && String(fromMethod).trim() !== '') {
      return fromMethod;
    }
  } catch {
    // Detached / graphics-layer features have no object id method.
  }
  const attrs = graphic.attributes ?? {};
  const field = objectIdField || 'OBJECTID';
  const value =
    attrs[field] ?? attrs.OBJECTID ?? attrs.objectid ?? attrs.FID ?? attrs.fid;
  if (value == null || String(value).trim() === '') return null;
  return typeof value === 'number' ? value : String(value);
}

export function buildClickedFeaturePickPayload(args: {
  meta: AiLayerPickPayload;
  graphic: __esri.Graphic;
  spatialFilter?: AiSpatialFilter;
}): AiLayerPickPayload {
  const { meta, graphic, spatialFilter } = args;
  const attrs = (graphic.attributes ?? {}) as Record<string, unknown>;
  const featureLayer =
    graphic.layer instanceof FeatureLayer ? graphic.layer : null;
  const objectIdField = featureLayer?.objectIdField || 'OBJECTID';

  if (meta.kind === 'internal') {
    const featureId = attrs.id != null ? String(attrs.id) : null;
    const featureName =
      (typeof attrs.name === 'string' && attrs.name.trim()) ||
      getExternalFeatureTitle(attrs);
    return {
      ...meta,
      ...(featureId
        ? { attributeFilters: [{ field: 'id', value: featureId }] }
        : {}),
      legendFilterLabel: featureName,
      pickedFeatureName: featureName,
      spatialFilterLabel: `Selected: ${featureName}`,
      featureSnapshot: [
        internalRowToAttributeSnapshot({
          id: featureId || String(attrs.OBJECTID ?? ''),
          name: typeof attrs.name === 'string' ? attrs.name : null,
          description:
            typeof attrs.description === 'string' ? attrs.description : null,
          resourceType:
            typeof attrs.resourceType === 'string' ? attrs.resourceType : null,
          properties: attrs.properties,
        }),
      ],
      featureSnapshotCoverage: 'complete',
      ...(spatialFilter
        ? { spatialFilter, spatialFilterLabel: `Selected: ${featureName}` }
        : {}),
    };
  }

  const featureId = getGraphicObjectId(graphic, objectIdField);
  const featureName = getExternalFeatureTitle(
    attrs,
    featureLayer?.displayField
  );
  return {
    ...meta,
    ...(featureId != null
      ? { attributeFilters: [{ field: objectIdField, value: featureId }] }
      : {}),
    legendFilterLabel: featureName,
    pickedFeatureName: featureName,
    spatialFilterLabel: `Selected: ${featureName}`,
    featureSnapshot: [graphicToAttributeSnapshot(graphic)],
    featureSnapshotCoverage: 'complete',
    ...(spatialFilter ? { spatialFilter } : {}),
  };
}

export function spatialFilterFromGeoJson(
  geometry: Geometry | null | undefined
): AiSpatialFilter | undefined {
  return isAreaGeometry(geometry) ? geometry : undefined;
}
